import { RefObject, useEffect, useRef } from "react"

type Props = {
  handler: () => void
}

export const useClickOutside = ({
  handler,
}: Props): RefObject<HTMLInputElement> => {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node

      // Ignore clicks that land inside a MUI Tooltip/Popper portal.
      // MUI renders tooltips and poppers as direct children of document.body
      // (portals), so target.closest('[role="tooltip"]') is truthy when the
      // user clicks on a tooltip label — even though those elements are
      // logically "inside" the component that owns the tooltip.
      const insideTooltip = (target as Element).closest?.('[role="tooltip"]')
      if (insideTooltip) return

      // Ignore clicks inside a MUI Popper portal (e.g. autocomplete dropdown).
      const insidePopper = (target as Element).closest?.('[data-popper-placement]')
      if (insidePopper) return

      if (ref.current && !ref.current.contains(target)) {
        handler()
      }
    }
    document.body.addEventListener("mousedown", handleClick)
    return () => {
      document.body.removeEventListener("mousedown", handleClick)
    }
  }, [])

  return ref
}
